import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
    Sparkles,
    UserPlus,
    PlayCircle,
    GraduationCap,
    FileText,
    Headphones,
    Book,
    Globe,
    ExternalLink,
    ChevronLeft,
    ChevronRight,
    Eye,
    Check,
} from "lucide-react";
import StatusControl from "@/components/StatusControl";

const TYPE_ICON = {
    video: PlayCircle, course: GraduationCap, article: FileText, doc: FileText,
    podcast: Headphones, book: Book, other: Globe, note: FileText,
};
const TYPE_LABEL = {
    video: "Video", course: "Course", article: "Article", doc: "Doc",
    podcast: "Podcast", book: "Book", other: "Link", note: "Note",
};
const TYPE_COVER = {
    video:   { from: "from-rose-400",    to: "to-orange-300" },
    course:  { from: "from-violet-500",  to: "to-fuchsia-400" },
    article: { from: "from-sky-400",     to: "to-cyan-300" },
    doc:     { from: "from-slate-500",   to: "to-slate-400" },
    podcast: { from: "from-amber-400",   to: "to-yellow-300" },
    book:    { from: "from-emerald-500", to: "to-teal-400" },
    other:   { from: "from-slate-400",   to: "to-slate-300" },
    note:    { from: "from-stone-400",   to: "to-stone-300" },
};
// (STATUS_META was removed when status UI moved into <StatusControl>.)

/**
 * "Assigned to you" focus section — sits at the top of /learning when the
 * caller has any individual resource grants (`is_my_grant === true`).
 *
 * Visually distinct from the Catalog: animated double border that pulses,
 * dark glassy background, holographic accents. Cards are horizontally
 * scrollable shelf-style so a long grant list doesn't push the catalog
 * below the fold.
 *
 * Renders nothing when there are no individual grants.
 */
const AssignedFocus = ({ resources = [], onResourcesChange, onOpenCollection }) => {
    const scroller = useRef(null);
    const openedRef = useRef(new Set());

    const items = useMemo(() => resources.filter((r) => r.is_my_grant), [resources]);
    if (items.length === 0) return null;

    const scroll = (dir) => {
        const el = scroller.current;
        if (!el) return;
        const step = Math.max(280, el.clientWidth * 0.85);
        el.scrollBy({ left: dir * step, behavior: "smooth" });
    };

    const handleOpen = async (r) => {
        if (r.url) window.open(r.url, "_blank", "noopener");
        // Fire-and-forget read-receipt ping so the assigner can see we
        // engaged with the resource (first_opened_at + opened_count).
        if (r.url) {
            try { api.post(`/learning/resources/${r.id}/open`).catch(() => {}); } catch { /* no-op */ }
        }
        if (r.my_status === "not_started" && !openedRef.current.has(r.id)) {
            openedRef.current.add(r.id);
            try {
                await api.put(`/learning/resources/${r.id}/status`, { status: "in_progress" });
                onResourcesChange?.((prev) => prev.map((x) => (x.id === r.id ? { ...x, my_status: "in_progress" } : x)));
            } catch { /* swallow */ }
        }
    };

    const setStatus = async (rid, status) => {
        // Optimistic — parent is single source of truth
        let undo;
        onResourcesChange?.((prev) => {
            undo = prev;
            return prev.map((x) => (x.id === rid ? { ...x, my_status: status } : x));
        });
        try {
            await api.put(`/learning/resources/${rid}/status`, { status });
        } catch {
            if (undo) onResourcesChange?.(undo);
        }
    };

    return (
        <section
            data-testid="learning-assigned-focus"
            className="relative rounded-2xl assigned-focus-wrap bg-gradient-to-br from-teal-50/60 via-white to-white text-slate-900 overflow-hidden border border-teal-100"
        >
            {/* Decorative glow — subtle on light theme */}
            <div className="absolute -top-12 -left-12 h-44 w-44 rounded-full bg-[#2EB4AC]/15 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-12 -right-12 h-44 w-44 rounded-full bg-[#2EB4AC]/10 blur-3xl pointer-events-none" />
            <div className="relative p-5 sm:p-6">
                <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
                    <div className="min-w-0">
                        <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.2em] font-bold bg-[#2EB4AC]/10 text-[#1F7F79] border border-[#2EB4AC]/30">
                            <Sparkles className="h-3 w-3" /> Focus
                        </div>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-2 flex items-center gap-3 text-slate-900">
                            <UserPlus className="h-6 w-6 text-[#2EB4AC]" />
                            Assigned to you
                            <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full text-xs font-bold bg-[#2EB4AC] text-white tabular-nums">
                                {items.length}
                            </span>
                        </h2>
                        <p className="text-sm text-slate-500 mt-1">
                            Specific resources your manager or teammates flagged for you. Start one to bump it to "In progress".
                        </p>
                    </div>
                    {items.length > 2 && (
                        <div className="hidden sm:flex items-center gap-1 shrink-0">
                            <button
                                type="button"
                                onClick={() => scroll(-1)}
                                aria-label="Scroll assigned left"
                                className="h-9 w-9 rounded-full bg-white border border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300 flex items-center justify-center transition-colors"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => scroll(1)}
                                aria-label="Scroll assigned right"
                                className="h-9 w-9 rounded-full bg-white border border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300 flex items-center justify-center transition-colors"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                    )}
                </div>

                <div
                    ref={scroller}
                    className="flex gap-4 overflow-x-auto scrollbar-thin scroll-smooth snap-x snap-mandatory pb-1"
                >
                    {items.map((r) => (
                        <div key={r.id} className="snap-start shrink-0 w-[280px] sm:w-[300px]">
                            <FocusCard r={r} onOpen={() => handleOpen(r)} onStatus={(s) => setStatus(r.id, s)} onCollection={() => onOpenCollection?.(r.collection_id)} />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

const formatShortDate = (iso) => {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        const now = new Date();
        if (d.toDateString() === now.toDateString()) return "today";
        const y = new Date(now); y.setDate(now.getDate() - 1);
        if (d.toDateString() === y.toDateString()) return "yesterday";
        return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch { return null; }
};

const FocusCard = ({ r, onOpen, onStatus, onCollection }) => {
    const Icon = TYPE_ICON[r.type] || Globe;
    const cover = TYPE_COVER[r.type] || TYPE_COVER.other;
    const img = r.preview?.image;

    return (
        <article
            data-testid={`assigned-focus-card-${r.id}`}
            className="group bg-white text-slate-900 rounded-xl overflow-hidden border border-teal-200 hover:border-[#2EB4AC] hover:shadow-md transition-all duration-200"
        >
            <button
                type="button"
                onClick={onOpen}
                className={`relative h-32 w-full overflow-hidden bg-gradient-to-br ${cover.from} ${cover.to} flex items-center justify-center`}
                aria-label={`Open ${r.name}`}
            >
                {img ? (
                    <>
                        <img src={img} alt="" className="absolute inset-0 h-full w-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                    </>
                ) : (
                    <div className="relative">
                        <div className="absolute inset-0 -m-4 rounded-full bg-white/15 blur-xl" />
                        <div className="relative h-12 w-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/25">
                            <Icon className="h-5 w-5 text-white" />
                        </div>
                    </div>
                )}
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-white/95 text-slate-800 border border-white/40">
                    <Icon className="h-3 w-3" /> {TYPE_LABEL[r.type] || r.type}
                </span>
                <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-[#2EB4AC] text-white">
                    <UserPlus className="h-3 w-3" /> For you
                </span>
            </button>
            <div className="p-3">
                <button type="button" onClick={onOpen} className="text-left w-full">
                    <h3 className="font-heading font-bold text-slate-900 text-sm leading-snug line-clamp-2 group-hover:text-[#25908A] transition-colors">{r.name}</h3>
                </button>
                <button
                    type="button"
                    onClick={onCollection}
                    className="mt-1.5 text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5 max-w-full truncate transition-colors"
                >
                    <span className="h-3.5 w-3.5 rounded-full bg-slate-100 text-slate-600 text-[8px] font-bold inline-flex items-center justify-center uppercase shrink-0">
                        {(r.collection_owner_name || "?").charAt(0)}
                    </span>
                    <span className="truncate">From {r.collection_name}</span>
                </button>
                {/* Engagement trail — shows the grantee their own footprint
                    so they know progress is tracked (and the assigner can
                    see it too via Receipts). */}
                {(r.my_first_opened_at || r.my_completed_at) && (
                    <div className="mt-2 inline-flex flex-wrap items-center gap-1.5">
                        {r.my_completed_at ? (
                            <span className="inline-flex items-center gap-1 px-1.5 h-5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <Check className="h-2.5 w-2.5" /> Done · {formatShortDate(r.my_completed_at)}
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 px-1.5 h-5 rounded text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                                <Eye className="h-2.5 w-2.5" /> Opened · {formatShortDate(r.my_first_opened_at)}
                            </span>
                        )}
                    </div>
                )}
                <div className="mt-3 pt-3 border-t border-slate-100">
                    <StatusControl
                        status={r.my_status}
                        onChange={onStatus}
                        data-testid={`focus-card-status-${r.id}`}
                        ringSize={38}
                    />
                </div>
                {r.url && (
                    <button
                        type="button"
                        onClick={onOpen}
                        className="mt-2 w-full inline-flex items-center justify-center gap-1 h-8 rounded-md text-[11px] font-semibold border border-slate-200 text-slate-700 hover:border-[#2EB4AC] hover:text-[#25908A] transition-colors"
                    >
                        Open <ExternalLink className="h-3 w-3" />
                    </button>
                )}
            </div>
        </article>
    );
};

export default AssignedFocus;
